import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  ComputerDesktopIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  XCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  WifiIcon,
  MapPinIcon,
  SignalIcon,
  CubeIcon,
  ClockIcon,
  BuildingOfficeIcon,
  Cog6ToothIcon,
  BoltIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import { useUserRole } from '@/hooks/useUserRole'
import { institutionsApi } from '@/api/institutionsApi'
import { 
  getTerminals, 
  createTerminal, 
  deleteTerminal, 
  restartTerminal, 
  getTerminalStats,
  getTerminalTypes,
  createTerminalType,
  deleteTerminalType,
  type RFIDTerminal,
  type TerminalType,
  type CreateTerminalRequest,
  type CreateTerminalTypeRequest,
  type TerminalStats
} from '@/api/rfidApi'
import toast from 'react-hot-toast'

// Form schema for adding new RFID terminal
const addTerminalSchema = z.object({
  name: z.string().min(2, 'Terminal name must be at least 2 characters'),
  serialNumber: z.string().min(1, 'Serial number is required'),
  location: z.string().min(1, 'Location is required'),
  ipAddress: z.string().ip('Valid IP address is required'),
  type: z.string().min(1, 'Terminal type is required'),
  institutionId: z.string().optional(),
  description: z.string().optional(),
})

type AddTerminalData = z.infer<typeof addTerminalSchema>

// Form schema for adding new terminal type
const addTerminalTypeSchema = z.object({
  name: z.string().min(2, 'Type name must be at least 2 characters'),
  description: z.string().min(1, 'Description is required'),
  color: z.string().min(1, 'Color is required'),
  icon: z.string().min(1, 'Icon identifier is required'),
})

type AddTerminalTypeData = z.infer<typeof addTerminalTypeSchema>

// Using imported interfaces from rfidApi

const RFIDTerminalsPage: React.FC = () => {
  const { user } = useAuth()
  const { userRole } = useUserRole()
  const [terminals, setTerminals] = useState<RFIDTerminal[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [institutionFilter, setInstitutionFilter] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showAddTypeModal, setShowAddTypeModal] = useState(false)
  const [showManageTypesModal, setShowManageTypesModal] = useState(false)
  const [selectedTerminal, setSelectedTerminal] = useState<RFIDTerminal | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid')
  
  // Available institutions for terminal assignment (super admin only)
  const [availableInstitutions, setAvailableInstitutions] = useState<{id: string, name: string}[]>([])
  
  // Terminal types management
  const [terminalTypes, setTerminalTypes] = useState<TerminalType[]>([])
  const [loadingTypes, setLoadingTypes] = useState(false)
  
  // Stats state
  const [stats, setStats] = useState<TerminalStats>({
    totalTerminals: 0,
    onlineTerminals: 0,
    offlineTerminals: 0,
    maintenanceTerminals: 0,
    errorTerminals: 0,
    byType: {},
    byInstitution: {},
    totalScansToday: 0
  })

  const addTerminalForm = useForm<AddTerminalData>({
    resolver: zodResolver(addTerminalSchema),
    defaultValues: {
      name: '',
      serialNumber: '',
      location: '',
      ipAddress: '',
      type: '',
      institutionId: '',
      description: '',
    }
  })

  const addTerminalTypeForm = useForm<AddTerminalTypeData>({
    resolver: zodResolver(addTerminalTypeSchema),
    defaultValues: {
      name: '',
      description: '',
      color: 'blue',
      icon: 'ComputerDesktopIcon',
    }
  })

  useEffect(() => {
    fetchTerminals()
    fetchTerminalTypes()
    if (userRole === 'super_admin') {
      fetchInstitutions()
    }
  }, [])

  useEffect(() => {
    // Re-fetch terminals when filters change
    const timeoutId = setTimeout(() => {
      fetchTerminals()
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [searchQuery, statusFilter, typeFilter, institutionFilter])

  const fetchTerminals = async () => {
    setLoading(true)
    try {
      console.log('Fetching RFID terminals...')
      
      const params: any = {}
      
      if (searchQuery.trim()) {
        params.search = searchQuery.trim()
      }
      
      if (statusFilter !== 'all') {
        params.status = statusFilter
      }
      
      if (typeFilter !== 'all') {
        params.type = typeFilter
      }
      
      if (institutionFilter !== 'all' && userRole === 'super_admin') {
        params.institutionId = institutionFilter
      }
      
      const [terminalsResponse, statsResponse] = await Promise.all([
        getTerminals(params),
        getTerminalStats()
      ])
      
      console.log('Terminals response:', terminalsResponse)
      console.log('Stats response:', statsResponse)
      
      setTerminals(terminalsResponse.data || [])
      setStats(statsResponse.data || {
        totalTerminals: 0,
        onlineTerminals: 0,
        offlineTerminals: 0,
        maintenanceTerminals: 0,
        errorTerminals: 0,
        byType: {},
        byInstitution: {},
        totalScansToday: 0
      })
      
    } catch (error: any) {
      console.error('Failed to fetch RFID terminals:', error)
      toast.error(error.response?.data?.message || 'Failed to load RFID terminals')
      setTerminals([])
      setStats({
        totalTerminals: 0,
        onlineTerminals: 0,
        offlineTerminals: 0,
        maintenanceTerminals: 0,
        errorTerminals: 0,
        byType: {},
        byInstitution: {},
        totalScansToday: 0
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchInstitutions = async () => {
    try {
      console.log('Fetching institutions for terminal assignment...')
      const res = await institutionsApi.getInstitutions()
      console.log('Institutions response:', res)
      
      // Normalize the response into an array of institution objects
      let institutionsData: any[] = []
      if (Array.isArray(res)) {
        institutionsData = res
      } else if (res && Array.isArray((res as any).data)) {
        institutionsData = (res as any).data
      } else if (res && Array.isArray((res as any).institutions)) {
        institutionsData = (res as any).institutions
      } else {
        institutionsData = []
      }
      
      const institutionsList = institutionsData.map(institution => ({
        id: String(institution.id ?? ''),
        name: String(institution.name ?? institution.title ?? 'Unnamed')
      }))
      
      setAvailableInstitutions(institutionsList)
      
      if (institutionsList.length === 0) {
        toast.error('No institutions found for terminal assignment')
      }
    } catch (error: any) {
      console.error('Failed to fetch institutions:', error)
      toast.error(error.response?.data?.message || 'Failed to load institutions')
      setAvailableInstitutions([])
    }
  }

  const fetchTerminalTypes = async () => {
    setLoadingTypes(true)
    try {
      console.log('Fetching terminal types...')
      
      const response = await getTerminalTypes()
      console.log('Terminal types response:', response)
      
      setTerminalTypes(response.data || [])
      
      if (response.data.length === 0) {
        toast.error('No terminal types found. Create some types first.')
      }
    } catch (error: any) {
      console.error('Failed to fetch terminal types:', error)
      toast.error(error.response?.data?.message || 'Failed to load terminal types')
      setTerminalTypes([])
    } finally {
      setLoadingTypes(false)
    }
  }

  const handleAddTerminalType = async (typeData: AddTerminalTypeData) => {
    try {
      console.log('Creating terminal type:', typeData)
      
      const createData: CreateTerminalTypeRequest = {
        name: typeData.name,
        description: typeData.description,
        color: typeData.color,
        icon: typeData.icon
      }
      
      const response = await createTerminalType(createData)
      console.log('Terminal type created:', response)
      
      toast.success(`Terminal type "${response.data.name}" created successfully!`)
      setShowAddTypeModal(false)
      addTerminalTypeForm.reset()
      fetchTerminalTypes() // Refresh the types list
      
    } catch (error: any) {
      console.error('Failed to create terminal type:', error)
      toast.error(error.response?.data?.message || 'Failed to create terminal type')
    }
  }

  const handleDeleteTerminalType = async (type: TerminalType) => {
    if (type.terminalCount > 0) {
      toast.error(`Cannot delete "${type.name}" - it's used by ${type.terminalCount} terminals`)
      return
    }
    
    if (confirm(`Are you sure you want to delete terminal type "${type.name}"?`)) {
      try {
        await deleteTerminalType(type.id)
        toast.success(`Terminal type "${type.name}" deleted successfully`)
        fetchTerminalTypes() // Refresh the types list
      } catch (error: any) {
        console.error('Failed to delete terminal type:', error)
        toast.error(error.response?.data?.message || 'Failed to delete terminal type')
      }
    }
  }

  const handleAddTerminal = async (terminalData: AddTerminalData) => {
    try {
      console.log('Creating RFID terminal:', terminalData)
      
      const createData: CreateTerminalRequest = {
        name: terminalData.name,
        serialNumber: terminalData.serialNumber,
        ipAddress: terminalData.ipAddress,
        type: terminalData.type,
        location: terminalData.location,
        description: terminalData.description,
        institutionId: terminalData.institutionId
      }
      
      const response = await createTerminal(createData)
      console.log('Terminal created:', response)
      
      toast.success(`Terminal "${response.data.name}" created successfully!`)
      setShowAddModal(false)
      addTerminalForm.reset()
      fetchTerminals() // Refresh the terminals list
      
    } catch (error: any) {
      console.error('Failed to create terminal:', error)
      toast.error(error.response?.data?.message || 'Failed to create terminal')
    }
  }

  const handleViewTerminal = (terminal: RFIDTerminal) => {
    setSelectedTerminal(terminal)
  }

  const handleEditTerminal = (terminal: RFIDTerminal) => {
    toast.success(`Edit "${terminal.name}" - Coming soon!`)
  }

  const handleDeleteTerminal = async (terminal: RFIDTerminal) => {
    if (confirm(`Are you sure you want to delete terminal "${terminal.name}"?`)) {
      try {
        await deleteTerminal(terminal.id)
        toast.success(`Terminal "${terminal.name}" deleted successfully`)
        fetchTerminals() // Refresh the terminals list
      } catch (error: any) {
        console.error('Failed to delete terminal:', error)
        toast.error(error.response?.data?.message || 'Failed to delete terminal')
      }
    }
  }

  const handleRestartTerminal = async (terminal: RFIDTerminal) => {
    try {
      console.log('Restarting terminal:', terminal.id)
      
      const response = await restartTerminal(terminal.id)
      console.log('Terminal restart response:', response)
      
      toast.success(`Terminal "${terminal.name}" restart initiated`)
      
      // Refresh terminals to show updated status
      setTimeout(() => {
        fetchTerminals()
      }, 2000)
    } catch (error: any) {
      console.error('Failed to restart terminal:', error)
      toast.error(error.response?.data?.message || 'Failed to restart terminal')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'bg-green-100 text-green-800'
      case 'offline':
        return 'bg-red-100 text-red-800'
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800'
      case 'error':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
        return <CheckCircleIcon className="h-4 w-4 text-green-600" />
      case 'offline':
        return <XCircleIcon className="h-4 w-4 text-red-600" />
      case 'maintenance':
        return <Cog6ToothIcon className="h-4 w-4 text-yellow-600" />
      case 'error':
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
      default:
        return <CubeIcon className="h-4 w-4 text-gray-600" />
    }
  }

  const getTypeColor = (typeName: string) => {
    const terminalType = terminalTypes.find(t => t.name === typeName)
    const color = terminalType?.color || 'gray'
    return `bg-${color}-100 text-${color}-800`
  }

  const getSignalStrengthColor = (strength: number) => {
    if (strength >= 80) return 'text-green-600'
    if (strength >= 60) return 'text-yellow-600'
    if (strength >= 40) return 'text-orange-600'
    return 'text-red-600'
  }

  const getBatteryColor = (level?: number) => {
    if (!level) return 'text-gray-400'
    if (level >= 60) return 'text-green-600'
    if (level >= 30) return 'text-yellow-600'
    return 'text-red-600'
  }

  const formatLastPing = (pingTime: string) => {
    const diff = Date.now() - new Date(pingTime).getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    return new Date(pingTime).toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mt-2 animate-pulse"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="dashboard-stat-card animate-pulse">
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
        <div className="dashboard-card animate-pulse">
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <ComputerDesktopIcon className="h-8 w-8 text-blue-600 mr-3" />
            RFID Terminals
          </h1>
          <p className="text-gray-600 mt-1">
            Monitor and manage RFID terminal devices across {userRole === 'super_admin' ? 'all institutions' : 'your institution'}
          </p>
        </div>
        <div className="flex space-x-3">
          {userRole === 'super_admin' && (
            <>
              <button 
                onClick={() => setShowManageTypesModal(true)}
                className="btn btn-secondary"
              >
                <Cog6ToothIcon className="h-4 w-4 mr-2" />
                Manage Types
              </button>
              <button 
                onClick={() => setShowAddModal(true)}
                className="btn btn-primary"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Terminal
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Terminals</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalTerminals}</p>
            </div>
            <ComputerDesktopIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Online</p>
              <p className="text-2xl font-bold text-green-600">{stats.onlineTerminals}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Offline</p>
              <p className="text-2xl font-bold text-red-600">{stats.offlineTerminals}</p>
            </div>
            <XCircleIcon className="h-8 w-8 text-red-600" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Maintenance</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.maintenanceTerminals}</p>
            </div>
            <Cog6ToothIcon className="h-8 w-8 text-yellow-600" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Errors</p>
              <p className="text-2xl font-bold text-red-600">{stats.errorTerminals}</p>
            </div>
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Scans Today</p>
              <p className="text-2xl font-bold text-blue-600">{stats.totalScansToday.toLocaleString()}</p>
            </div>
            <SignalIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col xl:flex-row gap-4 items-center justify-between">
        <div className="flex-1 relative max-w-md">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search terminals by name, location, IP, or serial..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input pl-10"
          />
        </div>
        
        <div className="flex items-center space-x-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Status</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="maintenance">Maintenance</option>
            <option value="error">Error</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Types</option>
            <option value="entry">Entry</option>
            <option value="exit">Exit</option>
            <option value="classroom">Classroom</option>
            <option value="cafeteria">Cafeteria</option>
            <option value="library">Library</option>
            <option value="admin">Admin</option>
          </select>

          {userRole === 'super_admin' && (
            <select
              value={institutionFilter}
              onChange={(e) => setInstitutionFilter(e.target.value)}
              className="form-input text-sm"
            >
              <option value="all">All Institutions</option>
              {availableInstitutions.map(institution => (
                <option key={institution.id} value={institution.id}>
                  {institution.name}
                </option>
              ))}
            </select>
          )}

          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded text-sm ${viewMode === 'grid' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-600'}`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded text-sm ${viewMode === 'table' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-600'}`}
            >
              Table
            </button>
          </div>
        </div>
      </div>

      {/* Terminals Grid View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {terminals.map((terminal) => (
            <motion.div
              key={terminal.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="dashboard-card hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => handleViewTerminal(terminal)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <ComputerDesktopIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{terminal.name}</h3>
                    <p className="text-sm text-gray-600">{terminal.location}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusIcon(terminal.status)}
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(terminal.status)}`}>
                    {terminal.status}
                  </span>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Type:</span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(terminal.type)}`}>
                    {terminal.type}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">IP Address:</span>
                  <span className="text-gray-900 font-mono text-xs">{terminal.ipAddress}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Serial:</span>
                  <span className="text-gray-900 font-mono text-xs">{terminal.serialNumber}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Signal:</span>
                  <span className={`font-medium ${getSignalStrengthColor(terminal.signalStrength)}`}>
                    {terminal.signalStrength}%
                  </span>
                </div>
                {terminal.batteryLevel && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Battery:</span>
                    <span className={`font-medium ${getBatteryColor(terminal.batteryLevel)}`}>
                      {terminal.batteryLevel}%
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="text-xs text-gray-500">
                  <div>Scans today: {terminal.dailyScans}</div>
                  <div>Last ping: {formatLastPing(terminal.lastPing)}</div>
                </div>
                <div className="flex space-x-1">
                  {userRole === 'super_admin' && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRestartTerminal(terminal)
                        }}
                        className="btn btn-ghost text-xs"
                        title="Restart Terminal"
                      >
                        <BoltIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleEditTerminal(terminal)
                        }}
                        className="btn btn-ghost text-xs"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteTerminal(terminal)
                        }}
                        className="btn btn-ghost text-xs text-red-600"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  {userRole === 'school_admin' && (
                    <span className="text-xs text-gray-500 px-2 py-1">View Only</span>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Terminals Table View */}
      {viewMode === 'table' && (
        <div className="dashboard-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Terminal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Network
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activity
                  </th>
                  {userRole === 'super_admin' && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Institution
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {terminals.map((terminal) => (
                  <motion.tr
                    key={terminal.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleViewTerminal(terminal)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                          <ComputerDesktopIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{terminal.name}</div>
                          <div className="text-sm text-gray-500">{terminal.location}</div>
                          <div className="text-xs text-gray-400 font-mono">{terminal.serialNumber}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-mono">{terminal.ipAddress}</div>
                      <div className="flex items-center space-x-1 mt-1">
                        <WifiIcon className={`h-4 w-4 ${getSignalStrengthColor(terminal.signalStrength)}`} />
                        <span className={`text-xs ${getSignalStrengthColor(terminal.signalStrength)}`}>
                          {terminal.signalStrength}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(terminal.status)}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(terminal.status)}`}>
                          {terminal.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatLastPing(terminal.lastPing)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">v{terminal.firmwareVersion}</div>
                      {terminal.batteryLevel && (
                        <div className="flex items-center space-x-1 mt-1">
                          <BoltIcon className={`h-4 w-4 ${getBatteryColor(terminal.batteryLevel)}`} />
                          <span className={`text-xs ${getBatteryColor(terminal.batteryLevel)}`}>
                            {terminal.batteryLevel}%
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{terminal.dailyScans} today</div>
                      <div className="text-xs text-gray-500">{terminal.totalScans.toLocaleString()} total</div>
                    </td>
                    {userRole === 'super_admin' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{terminal.institutionName}</div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        {userRole === 'super_admin' && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRestartTerminal(terminal)
                              }}
                              className="text-blue-600 hover:text-blue-900"
                              title="Restart Terminal"
                            >
                              <BoltIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleEditTerminal(terminal)
                              }}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteTerminal(terminal)
                              }}
                              className="text-red-600 hover:text-red-900"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        {userRole === 'school_admin' && (
                          <span className="text-xs text-gray-500">View Only</span>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {terminals.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card"
        >
          <div className="text-center py-12">
            <ComputerDesktopIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No RFID terminals found</h3>
            <p className="mt-2 text-gray-500">
              {searchQuery || statusFilter !== 'all' || typeFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : userRole === 'super_admin' 
                  ? 'Get started by adding your first RFID terminal'
                  : 'No RFID terminals assigned to your institution yet'
              }
            </p>
            {userRole === 'super_admin' && (
              <button 
                onClick={() => setShowAddModal(true)}
                className="mt-4 btn btn-primary"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add First Terminal
              </button>
            )}
            {userRole === 'school_admin' && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <ComputerDesktopIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h5 className="text-sm font-medium text-blue-900">Need RFID Terminals?</h5>
                    <p className="text-sm text-blue-700 mt-1">
                      Contact your platform administrator to have RFID terminals assigned to your institution.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Add Terminal Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Add RFID Terminal</h3>
                    <p className="text-sm text-gray-500">Register a new RFID terminal device</p>
                  </div>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={addTerminalForm.handleSubmit(handleAddTerminal)} className="p-6 space-y-6">
                
                {/* Terminal Information */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <ComputerDesktopIcon className="h-5 w-5 text-blue-600 mr-2" />
                    Terminal Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      {...addTerminalForm.register('name')}
                      label="Terminal Name"
                      placeholder="Main Entry Terminal"
                      error={addTerminalForm.formState.errors.name?.message}
                    />

                    <Input
                      {...addTerminalForm.register('serialNumber')}
                      label="Serial Number"
                      placeholder="RFID-001-ENT"
                      error={addTerminalForm.formState.errors.serialNumber?.message}
                    />

                    <Input
                      {...addTerminalForm.register('location')}
                      label="Physical Location"
                      placeholder="Main Gate, Room 5A, Cafeteria"
                      error={addTerminalForm.formState.errors.location?.message}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Terminal Type
                      </label>
                      <div className="flex space-x-2">
                        <select
                          {...addTerminalForm.register('type')}
                          className="form-input flex-1"
                          disabled={loadingTypes}
                        >
                          <option value="">
                            {loadingTypes ? 'Loading types...' : 'Select Terminal Type'}
                          </option>
                          {terminalTypes.filter(type => type.isActive).map(type => (
                            <option key={type.id} value={type.name}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setShowAddTypeModal(true)}
                          className="btn btn-ghost text-sm"
                          title="Add New Type"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                      </div>
                      {addTerminalForm.formState.errors.type && (
                        <p className="mt-1 text-sm text-red-600">{addTerminalForm.formState.errors.type.message}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        {terminalTypes.length} types available
                      </p>
                    </div>
                  </div>
                </div>

                {/* Network Configuration */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <WifiIcon className="h-5 w-5 text-green-600 mr-2" />
                    Network Configuration
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      {...addTerminalForm.register('ipAddress')}
                      label="IP Address"
                      placeholder="192.168.1.101"
                      error={addTerminalForm.formState.errors.ipAddress?.message}
                    />

                    {userRole === 'super_admin' ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Institution
                        </label>
                        <select
                          {...addTerminalForm.register('institutionId')}
                          className="form-input"
                        >
                          <option value="">Select Institution</option>
                          {availableInstitutions.map(institution => (
                            <option key={institution.id} value={institution.id}>
                              {institution.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-center">
                          <BuildingOfficeIcon className="h-5 w-5 text-blue-600 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-blue-800">Your Institution</p>
                            <p className="text-xs text-blue-600">Terminal will be assigned to your school automatically</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="md:col-span-2">
                      <Input
                        {...addTerminalForm.register('description')}
                        label="Description (Optional)"
                        placeholder="Additional details about this terminal"
                        error={addTerminalForm.formState.errors.description?.message}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium text-blue-900">Terminal Registration</h5>
                      <div className="text-sm text-blue-700 mt-1 space-y-1">
                        <p>• Terminal will be registered and ready for configuration</p>
                        <p>• Device will appear in monitoring dashboard immediately</p>
                        <p>• Initial firmware update may be required after registration</p>
                        <p>• Network connectivity will be tested during setup</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <ComputerDesktopIcon className="h-4 w-4 mr-2" />
                    Register Terminal
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Terminal Details Modal */}
      <AnimatePresence>
        {selectedTerminal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedTerminal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                      <ComputerDesktopIcon className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{selectedTerminal.name}</h3>
                      <p className="text-sm text-gray-500">{selectedTerminal.location} • {selectedTerminal.institutionName}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${getTypeColor(selectedTerminal.type)}`}>
                      {selectedTerminal.type}
                    </span>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(selectedTerminal.status)}
                      <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(selectedTerminal.status)}`}>
                        {selectedTerminal.status}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedTerminal(null)}
                    className="text-gray-400 hover:text-gray-600 ml-4"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Device Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Device Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Serial Number</span>
                        <span className="text-sm text-gray-900 font-mono">{selectedTerminal.serialNumber}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">IP Address</span>
                        <span className="text-sm text-gray-900 font-mono">{selectedTerminal.ipAddress}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Firmware</span>
                        <span className="text-sm text-gray-900">v{selectedTerminal.firmwareVersion}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Type</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(selectedTerminal.type)}`}>
                          {selectedTerminal.type}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Network & Performance */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Network & Performance</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Signal Strength</span>
                        <div className="flex items-center space-x-2">
                          <WifiIcon className={`h-4 w-4 ${getSignalStrengthColor(selectedTerminal.signalStrength)}`} />
                          <span className={`text-sm font-medium ${getSignalStrengthColor(selectedTerminal.signalStrength)}`}>
                            {selectedTerminal.signalStrength}%
                          </span>
                        </div>
                      </div>
                      {selectedTerminal.batteryLevel && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Battery Level</span>
                          <div className="flex items-center space-x-2">
                            <BoltIcon className={`h-4 w-4 ${getBatteryColor(selectedTerminal.batteryLevel)}`} />
                            <span className={`text-sm font-medium ${getBatteryColor(selectedTerminal.batteryLevel)}`}>
                              {selectedTerminal.batteryLevel}%
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Last Ping</span>
                        <span className="text-sm text-gray-900">{formatLastPing(selectedTerminal.lastPing)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(selectedTerminal.status)}
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedTerminal.status)}`}>
                            {selectedTerminal.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Activity Stats */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Activity Statistics</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Scans Today</span>
                        <span className="text-sm text-gray-900 font-bold">{selectedTerminal.dailyScans}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Total Scans</span>
                        <span className="text-sm text-gray-900 font-bold">{selectedTerminal.totalScans.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Avg. Daily</span>
                        <span className="text-sm text-gray-900">~{Math.floor(selectedTerminal.totalScans / 30)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Uptime</span>
                        <span className="text-sm text-green-600 font-medium">99.2%</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {selectedTerminal.description && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                    <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{selectedTerminal.description}</p>
                  </div>
                )}

                {/* System Information */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">System Information</h4>
                  <div className="bg-gray-50 rounded-lg p-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Created by: </span>
                      <span className="text-gray-900 font-medium">{selectedTerminal.createdBy}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Institution: </span>
                      <span className="text-gray-900 font-medium">{selectedTerminal.institutionName}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Registered: </span>
                      <span className="text-gray-900 font-medium">
                        {new Date(selectedTerminal.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Last Updated: </span>
                      <span className="text-gray-900 font-medium">
                        {new Date(selectedTerminal.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {userRole === 'super_admin' && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
                  <div className="flex justify-between">
                    <div className="flex space-x-3">
                      <Button
                        onClick={() => handleRestartTerminal(selectedTerminal)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <BoltIcon className="h-4 w-4 mr-2" />
                        Restart Terminal
                      </Button>
                    </div>
                    <div className="flex space-x-3">
                      <Button
                        onClick={() => handleEditTerminal(selectedTerminal)}
                        className="bg-indigo-600 hover:bg-indigo-700"
                      >
                        <PencilIcon className="h-4 w-4 mr-2" />
                        Edit Terminal
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              {userRole === 'school_admin' && (
                <div className="px-6 py-4 bg-blue-50 border-t border-blue-200 rounded-b-xl">
                  <div className="text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <EyeIcon className="h-5 w-5 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">Terminal Information View</span>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      Contact platform administrator for terminal management actions
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Manage Terminal Types Modal */}
      <AnimatePresence>
        {showManageTypesModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowManageTypesModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Manage Terminal Types</h3>
                    <p className="text-sm text-gray-500">Create and manage custom terminal types</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button 
                      onClick={() => setShowAddTypeModal(true)}
                      className="btn btn-primary btn-sm"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Add Type
                    </button>
                    <button
                      onClick={() => setShowManageTypesModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XCircleIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {terminalTypes.map((type) => (
                    <div key={type.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 bg-${type.color}-100 rounded-lg flex items-center justify-center`}>
                            <ComputerDesktopIcon className={`h-4 w-4 text-${type.color}-600`} />
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">{type.name}</h4>
                            <p className="text-xs text-gray-500">{type.terminalCount} terminals</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteTerminalType(type)}
                          className="text-red-400 hover:text-red-600"
                          disabled={type.terminalCount > 0}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{type.description}</p>
                      <div className="flex items-center justify-between text-xs">
                        <span className={`px-2 py-1 rounded-full bg-${type.color}-100 text-${type.color}-800`}>
                          {type.color}
                        </span>
                        <span className="text-gray-500">
                          {new Date(type.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {terminalTypes.length === 0 && !loadingTypes && (
                  <div className="text-center py-8">
                    <Cog6ToothIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">No terminal types</h3>
                    <p className="mt-2 text-gray-500">Create your first terminal type to get started</p>
                    <button 
                      onClick={() => setShowAddTypeModal(true)}
                      className="mt-4 btn btn-primary"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Add First Type
                    </button>
                  </div>
                )}

                {loadingTypes && (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-500">Loading terminal types...</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Terminal Type Modal */}
      <AnimatePresence>
        {showAddTypeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddTypeModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Add Terminal Type</h3>
                    <p className="text-sm text-gray-500">Create a new custom terminal type</p>
                  </div>
                  <button
                    onClick={() => setShowAddTypeModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={addTerminalTypeForm.handleSubmit(handleAddTerminalType)} className="p-6 space-y-4">
                <Input
                  {...addTerminalTypeForm.register('name')}
                  label="Type Name"
                  placeholder="Security Gate"
                  error={addTerminalTypeForm.formState.errors.name?.message}
                />

                <Input
                  {...addTerminalTypeForm.register('description')}
                  label="Description"
                  placeholder="Security checkpoints and access control"
                  error={addTerminalTypeForm.formState.errors.description?.message}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color Theme
                  </label>
                  <select
                    {...addTerminalTypeForm.register('color')}
                    className="form-input"
                  >
                    <option value="blue">Blue</option>
                    <option value="green">Green</option>
                    <option value="purple">Purple</option>
                    <option value="orange">Orange</option>
                    <option value="red">Red</option>
                    <option value="indigo">Indigo</option>
                    <option value="gray">Gray</option>
                    <option value="yellow">Yellow</option>
                  </select>
                  {addTerminalTypeForm.formState.errors.color && (
                    <p className="mt-1 text-sm text-red-600">{addTerminalTypeForm.formState.errors.color.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Icon Identifier
                  </label>
                  <select
                    {...addTerminalTypeForm.register('icon')}
                    className="form-input"
                  >
                    <option value="ComputerDesktopIcon">Computer Desktop</option>
                    <option value="ShieldCheckIcon">Security Shield</option>
                    <option value="AcademicCapIcon">Academic Cap</option>
                    <option value="CakeIcon">Food/Cafeteria</option>
                    <option value="BookOpenIcon">Book/Library</option>
                    <option value="MapPinIcon">Location Pin</option>
                    <option value="CubeIcon">Generic Device</option>
                  </select>
                  {addTerminalTypeForm.formState.errors.icon && (
                    <p className="mt-1 text-sm text-red-600">{addTerminalTypeForm.formState.errors.icon.message}</p>
                  )}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium text-blue-900">Custom Terminal Type</h5>
                      <div className="text-sm text-blue-700 mt-1 space-y-1">
                        <p>• Type will be available for all terminal creation</p>
                        <p>• Color and icon help identify terminal purposes</p>
                        <p>• Can be used across multiple institutions</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button
                    type="button"
                    onClick={() => setShowAddTypeModal(false)}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Create Type
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default RFIDTerminalsPage